import {
  MemberCampaignPostItemDTO,
  MemberMgSupplierCampaignItem,
  MemberMgSupplierCampaignItemLookup,
  MemberMgSupplierCampaignItemLookupDTO,
  MgSupplierItemLookupDTO,
} from '@retail/campaign/types';
import { MgSupplierItemSearchContainer } from '@retail/products/containers';
import {
  AddCampaignItemsSchema,
  MemberCampaignImportItem,
  MemberCampaignItemSchema,
  MemberCampaignItemsImportConfig,
  useGetAddMemberCampaignItemsColumns,
} from '@retail/campaign/components';
import { AddItemsToCampaignDialogBase } from './AddItemsToCampaignDialogBase';
import { useLanguageSelector } from '@retail/app/hooks';
import { useFormContext } from 'react-hook-form';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useLookupMemberCampaignItems } from '@retail/campaign/data-access';
import { useCallback, useState } from 'react';

interface Props {
  open: boolean;
  campaignId: string;
  onClose: () => void;
}

export const MemberCampaignDetailsAddItemsDialog = ({ campaignId, open, onClose }: Props) => {
  const { language } = useLanguageSelector();
  const { appTenant, assortmentId } = useSelectedOrgUnit();
  const getCampaignItemColumns = useGetAddMemberCampaignItemsColumns({
    externalSourceType: appTenant.externalItemSource,
  });

  const [lookupItemErrors, setLookupItemErrors] = useState<MemberMgSupplierCampaignItemLookupDTO[]>(
    []
  );

  const formMethods = useFormContext<AddCampaignItemsSchema<MemberCampaignItemSchema>>();

  const { mutateAsync: handleLookupItems, isPending: isPendingLookupItems } =
    useLookupMemberCampaignItems<MemberMgSupplierCampaignItemLookupDTO>({
      assortmentId,
    });

  const performItemsLookup = useCallback(
    async (dto: MgSupplierItemLookupDTO[]): Promise<MemberMgSupplierCampaignItem[]> => {
      const response = await handleLookupItems({
        body: { assortmentId, itemLookups: dto },
      });
      const itemsWithErrors = response.filter((item) => item.errorCode);
      if (itemsWithErrors.length) {
        setLookupItemErrors(itemsWithErrors);
        return [];
      }
      return response
        .map((dto) => MemberMgSupplierCampaignItemLookup.fromDto(dto, language))
        .flatMap(({ items }) => items ?? []);
    },
    [handleLookupItems, language, assortmentId]
  );

  return (
    <AddItemsToCampaignDialogBase<
      MemberMgSupplierCampaignItem,
      MemberCampaignPostItemDTO,
      MemberMgSupplierCampaignItemLookupDTO,
      MemberCampaignItemSchema,
      MemberCampaignImportItem
    >
      open={open}
      onClose={onClose}
      campaignId={campaignId}
      campaignType="member"
      importConfig={MemberCampaignItemsImportConfig.instance}
      itemsFormMethods={formMethods}
      renderItemSearch={({ selectedItems, setSelectedItems }) => (
        <MgSupplierItemSearchContainer
          assortmentId={assortmentId}
          checkedItems={selectedItems}
          setCheckedItems={setSelectedItems}
        />
      )}
      transformToPostDTO={(item, lookupItem) => ({
        mgSupplierId: lookupItem.supplier.id,
        mgItemNumber: lookupItem.mgItemNumber,
        campaignUnit: item.campaignUnit,
        includeVat: true,
        isSalesPriceOverridable: true,
        primaryDiscountPercentage: item.useDiscount ? item.primaryDiscountPercentage ?? null : null,
        campaignSalesPrice: !item.useDiscount ? item.campaignSalesPrice ?? null : null,
        campaignPurchasePrice: item.campaignPurchasePrice ?? undefined,
      })}
      getInitSchemaItem={(item) =>
        // @ts-ignore
        ({
          campaignUnit: item.getPrimaryPackageUnit()?.unit ?? item.packages[0].unit.unit ?? '',
          campaignSalesPrice: '',
          campaignPurchasePrice: '',
        } as MemberCampaignItemSchema)
      }
      mapLookupItemToSchemaItem={(lookupItem, importItem) =>
        ({
          campaignUnit: importItem?.campaignUnit ?? lookupItem.getPrimaryPackageUnit()?.unit ?? '-',
          campaignSalesPrice: importItem?.salesPrice ?? '',
          campaignPurchasePrice: importItem?.purchasePrice ?? '',
        } as MemberCampaignItemSchema)
      }
      getCampaignItemsColumns={getCampaignItemColumns}
      isPendingLookupItems={isPendingLookupItems}
      performItemsLookup={performItemsLookup}
      lookupItemErrors={lookupItemErrors}
      setLookupItemErrors={setLookupItemErrors}
    />
  );
};
